/**
 * Inline X9 brand mark.
 *
 * Replaces the previous `<img src="@x9/brand-assets/logo.svg" />`
 * loading because images don't inherit CSS `color` — the source SVG
 * has no `fill` attribute, so the browser defaults to black. On the
 * marine surfaces every entry screen renders against, a black logo
 * read as wrong against the surrounding white-on-marine type.
 *
 * Rendering the SVG inline (via the path data exposed by
 * `@x9/brand-assets/logo-data`) gives us `fill="currentColor"`, which
 * inherits the parent's `color` and matches the surrounding text by
 * construction. On `text-white` containers (every marine surface)
 * the logo is now white; on a hypothetical `text-x9-marine` light
 * surface it would automatically render dark.
 *
 * Sizing is driven by the parent's width — we deliberately don't set
 * explicit width/height, only `viewBox`, so the consuming `<div>`'s
 * Tailwind sizing (`w-[min(25vw,5.7rem)]`) governs the display size.
 */
// `@x9/brand-assets/logo-data` is a vanilla JS module; TypeScript can't
// infer its exports automatically. The cast below keeps consumers
// type-safe without requiring a types entry in the brand-assets pkg.
import {
  wordmarkPaths as wordmarkPathsRaw,
  wordmarkViewBox as wordmarkViewBoxRaw,
  logoPath as logoPathRaw,
  logoViewBox as logoViewBoxRaw,
} from '@x9/brand-assets/logo-data';

const logoPath = logoPathRaw as string;
const logoViewBox = logoViewBoxRaw as string;
const wordmarkPaths = wordmarkPathsRaw as string[];
const wordmarkViewBox = wordmarkViewBoxRaw as string;

interface X9LogoProps {
  /** Tailwind / utility classes; usually width + display + select-none. */
  className?: string;
  /**
   * Mark-only pillow, or the full horizontal wordmark (same art as the native
   * PDF reports and my.x9.app's sidebar). The mark is right where the logo is
   * one glyph among others; the wordmark is for chrome that names the product.
   */
  variant?: 'mark' | 'wordmark';
  /**
   * When provided, sets `role="img"` + `aria-label` for screen readers.
   * Omit for purely decorative usage (background mark, loading hero)
   * so AT bypasses the logo.
   */
  title?: string;
}

export default function X9Logo({ className, title, variant = 'mark' }: X9LogoProps) {
  const isWordmark = variant === 'wordmark';

  return (
    <svg
      viewBox={isWordmark ? wordmarkViewBox : logoViewBox}
      // `fill-current` (Tailwind shorthand for `fill: currentColor`)
      // is what makes the logo inherit the parent text color. Doing
      // this via className rather than the `fill` attribute keeps
      // override semantics consistent with the rest of the codebase.
      className={`fill-current ${className ?? ''}`}
      role={title ? 'img' : 'presentation'}
      aria-label={title}
      aria-hidden={title ? undefined : true}
    >
      {isWordmark ? (
        wordmarkPaths.map((d) => <path key={d.slice(0, 24)} d={d} />)
      ) : (
        <path d={logoPath} />
      )}
    </svg>
  );
}
